<script lang="ts">
import type { ContentFolder, Layout, LibraryType, MediaAssetRecord, Playlist } from '@codot/contracts';
import { foldersApi, layoutsApi, libraryApi, mediaApi, playlistsApi } from '../../api/content.api';
interface Library {
  assets: Map<string, MediaAssetRecord>;
  campaigns: Map<string, Layout>;
  playlists: Map<string, Playlist>;
}
const cache = new Map<boolean, { at: number; p: Promise<Library> }>();
/** One fetch shared by every picker opened within a minute. `withMedia` false: campaigns and playlists only (a
 *  server-paged picker gets its media with each page). */
function loadLibrary(withMedia = true): Promise<Library> {
  const hit = cache.get(withMedia);
  if (hit && Date.now() - hit.at < 60_000) return hit.p;
  const p = Promise.allSettled([withMedia ? mediaApi.list() : Promise.resolve([]), layoutsApi.list(), playlistsApi.list()]).then(([m, l, pl]) => ({
    assets: new Map(m.status === 'fulfilled' ? m.value.map((a) => [a.id, a] as const) : []),
    campaigns: new Map(l.status === 'fulfilled' ? l.value.map((c) => [c.id, c] as const) : []),
    playlists: new Map(pl.status === 'fulfilled' ? pl.value.map((x) => [x.id, x] as const) : []),
  }));
  cache.set(withMedia, { at: Date.now(), p });
  return p;
}

/** Server-paged picking (large libraries): rows come from GET content-library, searched and filtered there. */
export interface PickServer {
  /** Only what can be published to a player now (ready media, no audio). */
  playable?: boolean;
  /** Only ready media (audio included). */
  ready?: boolean;
  /** What may be picked (default: everything). */
  kinds?: ('media' | 'campaign' | 'playlist')[];
  /** Keys left out (e.g. the campaign being edited). */
  exclude?: string[];
  /** Shown before the library's rows (e.g. uploads still optimizing). */
  pinned?: PickItem[];
  /** Per-row note, by key ("media:…"). */
  note?: (key: string) => string | null;
  /** Why an item won't play where it's going (e.g. a player that can't show compositions): flagged on its card. */
  wontPlay?: (asset: MediaAssetRecord) => string | null;
}
</script>

<script setup lang="ts">
/**
 * Shared content picker (Phase 16 Step 9; legacy G1/I7/P4). Search, type chips, thumbnails, each item's
 * folder path, "N selected" / Clear, 50 then "Show more". Built to be reused by the Interactivity target
 * picker (Step 11). Either filters the `items` it is given, or (`server`) pages the library from the server.
 */
import { useI18n } from 'vue-i18n';
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import { pathLabel } from '../../utils/folders';
import CampaignThumb from '../CampaignThumb.vue';
import PlaylistThumb from '../PlaylistThumb.vue';
import ZoneWidget from '../apps/ZoneWidget.vue';
import CompositionThumb from '../composition/CompositionThumb.vue';

export type PickType = 'playlist' | 'campaign' | 'app' | 'video' | 'image';
export interface PickItem {
  key: string;
  name: string;
  type: PickType;
  thumbnailUrl?: string | null;
  /** "Content › Ramadan" — omitted for top-level items. */
  path?: string | null;
  /** e.g. "Already in this list". */
  note?: string | null;
}

const { t } = useI18n();
const props = withDefaults(defineProps<{ items?: PickItem[]; query?: string; server?: PickServer }>(), { items: () => [], query: undefined, server: undefined });
const picked = defineModel<string[]>({ required: true });

/* Previews for items without an image: the library, loaded once per page (keys are "media:…", "campaign:…", "playlist:…"). */
const refOf = (key: string) => {
  const m = /^(media|campaign|playlist):(.+)$/.exec(key);
  return m ? { kind: m[1] as 'media' | 'campaign' | 'playlist', id: m[2]! } : null;
};
const lib = shallowRef<Library | null>(null);
onMounted(async () => {
  if (props.server) {
    const [l, f] = await Promise.all([loadLibrary(false), foldersApi.list().catch(() => [])]);
    folders.value = f;
    lib.value = { ...l, assets: new Map([...l.assets, ...serverMedia.value]) };
  } else if (props.items.some((i) => !i.thumbnailUrl || i.type === 'playlist')) lib.value = await loadLibrary();
});

const TYPES = computed<{ value: PickType | 'all'; label: string }[]>(() => [
  { value: 'all', label: t('publish.tAll') },
  { value: 'playlist', label: t('publish.tPlaylists') },
  { value: 'campaign', label: t('publish.tCampaigns') },
  { value: 'app', label: t('publish.tApps') },
  { value: 'video', label: t('publish.tVideos') },
  { value: 'image', label: t('publish.tImages') },
]);
const BADGE: Record<PickType, string> = {
  app: 'bg-pink-100 text-pink-800 dark:bg-pink-950 dark:text-pink-300',
  campaign: 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300',
  playlist: 'bg-teal-100 text-teal-800 dark:bg-teal-950 dark:text-teal-300',
  video: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  image: 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300',
};
const type = ref<PickType | 'all'>('all');
const own = ref('');
const q = computed(() => (props.query ?? own.value).trim().toLowerCase());

/* ---------------- server mode ---------------- */
const SERVER_TYPES: Record<PickType, LibraryType[]> = { playlist: ['playlist'], campaign: ['campaign'], app: ['app', 'composition'], video: ['video'], image: ['image'] };
/** The library types the caller allows (`kinds`), or null for every type. */
const allowedTypes = computed<LibraryType[] | null>(() => {
  const k = props.server?.kinds;
  if (!k) return null;
  return [...(k.includes('playlist') ? (['playlist'] as const) : []), ...(k.includes('campaign') ? (['campaign'] as const) : []), ...(k.includes('media') ? (['image', 'video', 'app', 'composition', 'pdf'] as const) : [])];
});
const folders = shallowRef<ContentFolder[]>([]);
const serverRows = shallowRef<PickItem[]>([]);
const serverMedia = shallowRef(new Map<string, MediaAssetRecord>());
const serverTotal = ref(0);
const serverNext = ref<number | null>(null);
const serverCounts = ref<Record<string, number>>({});
let generation = 0;
async function fetchServer(offset: number): Promise<void> {
  const gen = offset === 0 ? ++generation : generation;
  const ty = type.value;
  const allowed = allowedTypes.value;
  const types = ty === 'all' ? allowed : SERVER_TYPES[ty].filter((x) => !allowed || allowed.includes(x));
  const page = await libraryApi.page({
    q: q.value,
    ...(types ? { types: types.join(',') } : {}),
    ready: props.server?.ready ? 'true' : 'false',
    // Search and type filters look everywhere; browsing without them would only show the top level.
    folderId: undefined,
    playable: props.server?.playable ? 'true' : 'false',
    offset,
  });
  if (gen !== generation) return;
  const media = new Map(page.media.map((a) => [a.id, a] as const));
  serverMedia.value = new Map([...(offset ? serverMedia.value : []), ...media]);
  if (lib.value) lib.value = { ...lib.value, assets: new Map([...lib.value.assets, ...media]) };
  const rows = page.items.map((r): PickItem => {
    const key = `${r.kind}:${r.id}`;
    const m = r.kind === 'media' ? media.get(r.id) : undefined;
    return {
      key,
      name: r.name,
      type: (r.type === 'composition' ? 'app' : r.type) as PickType,
      thumbnailUrl: m?.thumbnailUrl ?? null,
      path: r.folderId,
    };
  });
  serverRows.value = offset ? [...serverRows.value, ...rows] : rows;
  serverTotal.value = page.total;
  serverNext.value = page.nextOffset;
  if (page.summary && ty === 'all' && !q.value) {
    const c = page.summary.counts;
    const ok = (x: LibraryType) => !allowed || allowed.includes(x);
    serverCounts.value = {
      all: page.total,
      playlist: ok('playlist') ? c.playlist : 0,
      campaign: ok('campaign') ? c.campaign : 0,
      app: ok('app') ? c.app + c.composition : 0,
      video: ok('video') ? c.video : 0,
      image: ok('image') ? c.image : 0,
    };
  }
}
let searchTimer: ReturnType<typeof setTimeout> | undefined;
if (props.server) {
  void fetchServer(0).catch(() => undefined);
  watch(type, () => void fetchServer(0).catch(() => undefined));
  watch(q, () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => void fetchServer(0).catch(() => undefined), 250);
  });
}
onBeforeUnmount(() => clearTimeout(searchTimer));

const shown = computed(() =>
  props.server
    ? [
        ...(props.server.pinned ?? []),
        // `path` holds the folder id until here: folders and notes may arrive after the rows.
        ...serverRows.value
          .filter((r) => !props.server!.pinned?.some((p) => p.key === r.key) && !props.server!.exclude?.includes(r.key))
          .map((r) => ({ ...r, path: r.path ? pathLabel(folders.value, r.path) : null, note: props.server!.note?.(r.key) ?? null })),
      ]
    : props.items.filter((i) => (type.value === 'all' || i.type === type.value) && (!q.value || `${i.name} ${i.path ?? ''}`.toLowerCase().includes(q.value))),
);
const counts = computed(() =>
  props.server
    ? serverCounts.value
    : Object.fromEntries(TYPES.value.map((ty) => [ty.value, ty.value === 'all' ? props.items.length : props.items.filter((i) => i.type === ty.value).length])),
);
const PAGE = 50;
const limit = ref(PAGE);
watch([q, type], () => (limit.value = PAGE));
const page = computed(() => (props.server ? shown.value : shown.value.slice(0, limit.value)));
/** Rows not shown yet. */
const left = computed(() => (props.server ? serverTotal.value - serverRows.value.length : shown.value.length - page.value.length));
let loadingMore = false;
async function showMore(): Promise<void> {
  if (!props.server) {
    limit.value += PAGE;
    return;
  }
  if (loadingMore || serverNext.value === null) return;
  loadingMore = true;
  await fetchServer(serverNext.value).catch(() => undefined);
  loadingMore = false;
}
/** The card's "won't play" flag (media only; the reason goes in its tooltip). */
const flagOf = (k: string): string | null => {
  const r = refOf(k);
  const a = r?.kind === 'media' ? lib.value?.assets.get(r.id) : undefined;
  return a && props.server?.wontPlay ? props.server.wontPlay(a) : null;
};
const toggle = (k: string, name?: string) => {
  if (name) names.set(k, name);
  picked.value = picked.value.includes(k) ? picked.value.filter((x) => x !== k) : [...picked.value, k];
};
/* Several items play in the order they're picked: shown on each card and changeable in the Play order list. */
const names = new Map<string, string>();
const nameOf = (k: string) => names.get(k) ?? shown.value.find((c) => c.key === k)?.name ?? k;
const orderOf = (k: string) => picked.value.indexOf(k) + 1;
/* Drag a row to reorder (touch: press and hold, via the app's drag polyfill), as in Content In Player. */
const dragKey = ref<string | null>(null);
const overKey = ref<string | null>(null);
function onOrderDragStart(e: DragEvent, k: string): void {
  dragKey.value = k;
  e.dataTransfer?.setData('text/plain', k);
  if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
}
function onOrderDragOver(e: DragEvent, k: string): void {
  if (!dragKey.value) return;
  e.preventDefault();
  overKey.value = k;
}
function onOrderDrop(e: DragEvent, target: string): void {
  e.preventDefault();
  const from = dragKey.value;
  dragKey.value = overKey.value = null;
  if (!from || from === target) return;
  const before = picked.value;
  const next = before.filter((k) => k !== from);
  next.splice(next.indexOf(target) + (before.indexOf(from) < before.indexOf(target) ? 1 : 0), 0, from);
  picked.value = next;
}
function move(i: number, by: -1 | 1): void {
  const next = [...picked.value];
  const j = i + by;
  if (j < 0 || j >= next.length) return;
  [next[i], next[j]] = [next[j]!, next[i]!];
  picked.value = next;
}
const label = (ty: PickType) => t(`content.type_${ty}`);
</script>

<template>
  <div class="space-y-2">
    <input v-if="query === undefined" v-model="own" type="search" class="input py-1.5" :placeholder="t('publish.searchContent')" :aria-label="t('publish.searchContent')" />
    <div class="flex flex-wrap items-center gap-1.5" role="radiogroup" :aria-label="t('publish.contentType')">
      <button
        v-for="ty in TYPES.filter((x) => x.value === 'all' || counts[x.value] || (server && type === x.value))"
        :key="ty.value"
        type="button"
        role="radio"
        :aria-checked="type === ty.value"
        class="rounded-full border px-2.5 py-0.5 text-xs"
        :class="type === ty.value ? 'border-indigo-500 bg-indigo-50 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-200' : 'border-slate-300 dark:border-slate-700'"
        @click="type = ty.value"
      >
        {{ ty.label }} <span class="text-slate-400">{{ counts[ty.value] }}</span>
      </button>
      <span class="ms-auto text-xs text-slate-500" aria-live="polite">
        {{ t('publish.nSelected', { n: picked.length }) }}
        <button v-if="picked.length" type="button" class="ms-1 text-indigo-600 hover:underline dark:text-indigo-400" @click="picked = []">{{ t('publish.clear') }}</button>
      </span>
    </div>
    <ul class="grid max-h-[26rem] grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
      <li v-for="c in page" :key="c.key">
        <label
          class="flex h-full cursor-pointer flex-col overflow-hidden rounded-lg border text-xs"
          :class="picked.includes(c.key) ? (flagOf(c.key) ? 'border-amber-500 ring-2 ring-amber-500/30' : 'border-indigo-500 ring-2 ring-indigo-500/20') : 'border-slate-200 hover:border-slate-300 dark:border-slate-800'"
          :title="flagOf(c.key) ?? undefined"
        >
          <span class="flex items-center gap-1.5 px-2 py-1.5">
            <input type="checkbox" class="h-3.5 w-3.5 accent-indigo-600" :checked="picked.includes(c.key)" @change="toggle(c.key, c.name)" />
            <span class="min-w-0 flex-1 truncate font-medium" :title="c.name">{{ c.name }}</span>
          </span>
          <span class="relative flex aspect-video items-center justify-center bg-slate-100 dark:bg-slate-800">
            <img v-if="c.thumbnailUrl && c.type !== 'playlist'" :src="c.thumbnailUrl" alt="" class="absolute inset-0 h-full w-full object-cover" loading="lazy" />
            <!-- No image: the same previews as the library (campaign layouts, playlist mosaics, live app stills). -->
            <CampaignThumb v-else-if="lib && refOf(c.key)?.kind === 'campaign' && lib.campaigns.get(refOf(c.key)!.id)" :campaign="lib.campaigns.get(refOf(c.key)!.id)!" :assets="lib.assets" class="absolute inset-0" />
            <PlaylistThumb v-else-if="lib && refOf(c.key)?.kind === 'playlist' && lib.playlists.get(refOf(c.key)!.id)" :playlist="lib.playlists.get(refOf(c.key)!.id)!" :assets="lib.assets" :campaigns="lib.campaigns" class="absolute inset-0" />
            <ZoneWidget v-else-if="lib && refOf(c.key)?.kind === 'media' && lib.assets.get(refOf(c.key)!.id)?.config" :config="lib.assets.get(refOf(c.key)!.id)!.config!" :playing="false" :width="1920" :height="1080" class="pointer-events-none absolute inset-0" />
            <CompositionThumb v-else-if="lib && refOf(c.key)?.kind === 'media' && lib.assets.get(refOf(c.key)!.id)?.composition" :doc="lib.assets.get(refOf(c.key)!.id)!.composition!.doc" :library="lib.assets" class="pointer-events-none absolute inset-0" />
            <span class="badge absolute start-1.5 bottom-1.5" :class="BADGE[c.type]">{{ label(c.type) }}</span>
            <span
              v-if="flagOf(c.key)"
              class="absolute start-1.5 top-1.5 inline-flex items-center gap-1 rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-semibold text-amber-950 shadow"
              :title="flagOf(c.key) ?? undefined"
              ><span aria-hidden="true">⚠</span>{{ t('plays.flag') }}</span
            >
            <span
              v-if="picked.length > 1 && picked.includes(c.key)"
              class="absolute end-1.5 top-1.5 flex h-6 min-w-6 items-center justify-center rounded-full bg-indigo-600 px-1.5 text-xs font-semibold text-white shadow"
              :title="t('publish.playsNth', { n: orderOf(c.key) })"
              >{{ orderOf(c.key) }}</span
            >
          </span>
          <span v-if="c.path" class="truncate px-2 py-1 text-[10px] text-slate-500" :title="c.path">📁 {{ c.path }}</span>
          <span v-if="c.note" class="bg-emerald-50 px-2 py-1 text-[10px] text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">{{ c.note }}</span>
          <!-- Picked anyway: say why it won't play right on the card, not only in the tooltip. -->
          <span v-if="picked.includes(c.key) && flagOf(c.key)" class="bg-amber-50 px-2 py-1 text-[10px] leading-snug text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">{{ flagOf(c.key) }}</span>
        </label>
      </li>
      <li v-if="!shown.length" class="col-span-full py-6 text-center text-sm text-slate-500">{{ t('publish.nothingMatches') }}</li>
    </ul>
    <button v-if="left > 0" type="button" class="text-sm text-indigo-600 hover:underline dark:text-indigo-400" @click="showMore">
      {{ t('publish.showMore', { n: left }) }}
    </button>
    <section v-if="picked.length > 1" class="rounded-lg border border-slate-200 p-3 dark:border-slate-800" :aria-label="t('publish.playOrder')">
      <h3 class="text-sm font-medium">{{ t('publish.playOrder') }}</h3>
      <p class="mb-2 text-xs text-slate-500">{{ t('publish.playOrderHint') }}</p>
      <ol class="space-y-1">
        <li
          v-for="(k, i) in picked"
          :key="k"
          class="flex cursor-grab items-center gap-2 rounded-md px-2 py-1 text-sm transition-colors active:cursor-grabbing"
          :class="[overKey === k && dragKey !== k ? 'bg-indigo-100 ring-1 ring-indigo-400 dark:bg-indigo-950/60' : 'bg-slate-50 dark:bg-slate-800/60', dragKey === k ? 'opacity-50' : '']"
          draggable="true"
          @dragstart="onOrderDragStart($event, k)"
          @dragover="onOrderDragOver($event, k)"
          @dragenter.prevent
          @dragleave="overKey === k && (overKey = null)"
          @drop="onOrderDrop($event, k)"
          @dragend="dragKey = overKey = null"
        >
          <span
            class="px-0.5 text-slate-400"
            tabindex="0"
            role="button"
            :aria-label="t('publish.dragOrder', { name: nameOf(k), n: i + 1 })"
            :title="t('publish.dragOrderTip')"
            @keydown.alt.up.prevent="move(i, -1)"
            @keydown.alt.down.prevent="move(i, 1)"
            >≡</span
          >
          <span class="flex h-5 min-w-5 items-center justify-center rounded-full bg-indigo-600 px-1 text-[11px] font-semibold text-white">{{ i + 1 }}</span>
          <span class="min-w-0 flex-1 truncate" :title="nameOf(k)">{{ nameOf(k) }}</span>
          <button type="button" class="rounded px-1.5 text-slate-500 hover:bg-slate-200 disabled:opacity-30 dark:hover:bg-slate-700" :disabled="i === 0" :aria-label="t('publish.moveUp', { name: nameOf(k) })" @click="move(i, -1)">↑</button>
          <button type="button" class="rounded px-1.5 text-slate-500 hover:bg-slate-200 disabled:opacity-30 dark:hover:bg-slate-700" :disabled="i === picked.length - 1" :aria-label="t('publish.moveDown', { name: nameOf(k) })" @click="move(i, 1)">↓</button>
          <button type="button" class="rounded px-1.5 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700" :aria-label="t('publish.unpick', { name: nameOf(k) })" @click="toggle(k)">✕</button>
        </li>
      </ol>
    </section>
  </div>
</template>
